import React, { useEffect, useState } from 'react';
import {
  X, Users, Globe, RefreshCw, Eye, Monitor, Smartphone, Tablet,
  Clock, MapPin, Wifi, ChevronLeft, ChevronRight, Info,
  Database, CheckCircle2, AlertCircle, Copy, Check
} from 'lucide-react';
import {
  type VisitorRecord,
  supabaseConfigured,
  fetchTotalCount,
  fetchSupabaseVisitors,
  loadLocalRecords,
} from '../utils/visitorTracker';

interface DevPanelProps { onClose: () => void; }
type Tab = 'overview' | 'visitors' | 'current' | 'setup';

// ── Small helpers ────────────────────────────────────────────────────────────

function DeviceIcon({ device }: { device: string }) {
  if (device === 'Mobile')  return <Smartphone size={12} className="text-blue-500" />;
  if (device === 'Tablet')  return <Tablet     size={12} className="text-purple-500" />;
  return                           <Monitor    size={12} className="text-gray-500" />;
}

function Flag({ code }: { code: string }) {
  if (!code) return null;
  try {
    const cp = code.toUpperCase().split('').map((c) => 0x1F1E6 + c.charCodeAt(0) - 65);
    return <span>{String.fromCodePoint(...cp)}</span>;
  } catch { return null; }
}

function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleString('zh-CN', {
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
}

function Row({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs py-1.5 border-b border-gray-50 last:border-0">
      <span className="w-28 flex-shrink-0 text-gray-400 font-medium">{label}</span>
      <span className="text-gray-800 break-all">{value}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-lg p-3 text-center">
      <p className="text-xl font-bold text-gray-800">{value}</p>
      <p className="text-[10px] text-gray-400 mt-0.5">{label}</p>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="ml-1.5 p-1 rounded hover:bg-gray-200 transition-colors flex-shrink-0"
    >
      {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} className="text-gray-400" />}
    </button>
  );
}

// ── SQL setup script ─────────────────────────────────────────────────────────

const SETUP_SQL = `-- Run this in Supabase → SQL Editor
CREATE TABLE IF NOT EXISTS visitors (
  id               TEXT PRIMARY KEY,
  timestamp        TIMESTAMPTZ DEFAULT now(),
  ip               TEXT,
  country          TEXT,
  country_code     TEXT,
  region           TEXT,
  city             TEXT,
  timezone         TEXT,
  isp              TEXT,
  browser          TEXT,
  browser_version  TEXT,
  os               TEXT,
  device           TEXT,
  language         TEXT,
  referrer         TEXT,
  screen_resolution TEXT,
  viewport         TEXT,
  page_url         TEXT
);

-- Enable Row Level Security
ALTER TABLE visitors ENABLE ROW LEVEL SECURITY;

-- Allow anyone to INSERT (browser tracking)
CREATE POLICY "allow_insert"
  ON visitors FOR INSERT TO anon WITH CHECK (true);

-- Allow anyone to SELECT (DevPanel can read all data)
CREATE POLICY "allow_select"
  ON visitors FOR SELECT TO anon USING (true);`;

// ── Main Component ────────────────────────────────────────────────────────────

export default function DevPanel({ onClose }: DevPanelProps) {
  const [tab, setTab]               = useState<Tab>(supabaseConfigured ? 'overview' : 'setup');
  const [totalCount, setTotalCount] = useState<number | null>((window as any).__visitCount ?? null);
  const [loading, setLoading]       = useState(false);
  const [visitors, setVisitors]     = useState<VisitorRecord[]>([]);
  const [page, setPage]             = useState(0);
  const PAGE_SIZE = 8;

  const refresh = async () => {
    setLoading(true);
    const [count, remote] = await Promise.all([
      fetchTotalCount(),
      supabaseConfigured ? fetchSupabaseVisitors(500) : Promise.resolve([]),
    ]);
    if (count !== null) setTotalCount(count);
    if (supabaseConfigured && remote.length > 0) {
      setVisitors(remote);
    } else {
      setVisitors(loadLocalRecords().reverse());
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const pageCount = Math.ceil(visitors.length / PAGE_SIZE);
  const pageItems = visitors.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const current   = visitors[0];

  // ── Analytics helpers ────────────────────────────────────────────────────
  const countBy = <K extends keyof VisitorRecord>(key: K) => {
    const map: Record<string, number> = {};
    visitors.forEach((v) => {
      const val = String(v[key] || 'Unknown');
      map[val] = (map[val] ?? 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  };

  const uniqueIPs = new Set(
    visitors.map((v) => v.ip).filter((ip) => ip && ip !== 'Fetching…' && ip !== 'Unavailable')
  ).size;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: '总览' },
    { id: 'visitors', label: `记录 (${visitors.length})` },
    { id: 'current',  label: '当前' },
    { id: 'setup',    label: supabaseConfigured ? '✓ 后端' : '⚠ 配置' },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-[94vw] max-w-xl flex flex-col overflow-hidden"
        style={{ maxHeight: '88vh' }}
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Eye size={15} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h2 className="text-sm font-bold text-gray-900 leading-none">Developer Panel</h2>
                {supabaseConfigured && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                    <Database size={9} /> Supabase
                  </span>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-0.5">Ctrl+Shift+D · window.showDevPanel()</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={refresh} disabled={loading}
              className="p-1.5 rounded hover:bg-gray-100 transition-colors" title="Refresh">
              <RefreshCw size={13} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose}
              className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
              <X size={14} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-3 flex-shrink-0 bg-white">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
                tab === t.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">

          {/* ─ Overview ─ */}
          {tab === 'overview' && (
            <div className="space-y-3">
              {/* Total */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={15} className="text-blue-600" />
                  <span className="text-xs font-semibold text-gray-600">
                    Total Visits {supabaseConfigured ? '(Supabase · cross-device)' : '(CounterAPI · cross-device)'}
                  </span>
                </div>
                {loading
                  ? <div className="flex items-center gap-2 mt-1"><RefreshCw size={13} className="text-blue-400 animate-spin" /><span className="text-xs text-gray-400">Loading…</span></div>
                  : <p className="text-3xl font-bold text-blue-600 mt-0.5">{totalCount?.toLocaleString() ?? '—'}</p>
                }
              </div>

              {/* Quick stats */}
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                  {supabaseConfigured ? 'Supabase 全量数据' : '本设备数据 (localStorage)'}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="访问记录" value={visitors.length} />
                  <Stat label="唯一 IP" value={uniqueIPs || '—'} />
                  <Stat label="国家/地区" value={countBy('country').filter(([k]) => k !== 'Unknown' && k !== '').length || '—'} />
                </div>
              </div>

              {/* Device breakdown */}
              {visitors.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wider">设备类型</p>
                  <div className="grid grid-cols-3 gap-2">
                    {(['Mobile', 'Tablet', 'Desktop'] as const).map((d) => {
                      const n = visitors.filter((v) => v.device === d).length;
                      return (
                        <div key={d} className="bg-white rounded-lg p-2.5 text-center">
                          <DeviceIcon device={d} />
                          <p className="text-base font-bold text-gray-800 mt-1">{n}</p>
                          <p className="text-[10px] text-gray-400">{d}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top countries */}
              {visitors.length > 0 && (() => {
                const top = countBy('country').filter(([k]) => k !== 'Unknown' && k !== '').slice(0, 6);
                if (!top.length) return null;
                const codeMap = Object.fromEntries(
                  visitors.map((v) => [v.country, v.country_code]).filter(([k]) => k)
                );
                return (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Globe size={12} className="text-gray-500" />
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Top 国家/地区</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {top.map(([country, count]) => (
                        <div key={country} className="flex items-center gap-1.5 bg-white rounded-lg px-2.5 py-1.5">
                          <Flag code={codeMap[country] ?? ''} />
                          <span className="text-xs text-gray-700 flex-1 truncate">{country}</span>
                          <span className="text-xs font-bold text-gray-500">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Top browsers */}
              {visitors.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wider">浏览器分布</p>
                  <div className="space-y-1.5">
                    {countBy('browser').slice(0, 5).map(([browser, count]) => {
                      const pct = Math.round(count / visitors.length * 100);
                      return (
                        <div key={browser} className="flex items-center gap-2">
                          <span className="text-xs text-gray-700 w-16 flex-shrink-0">{browser}</span>
                          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-500 w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─ Visitors list ─ */}
          {tab === 'visitors' && (
            <div>
              {visitors.length === 0
                ? <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                    <Info size={28} className="mb-2 opacity-30" />
                    <p className="text-sm">{supabaseConfigured ? '暂无数据' : '仅保存本设备访问记录'}</p>
                  </div>
                : <>
                    <p className="text-[10px] text-gray-400 mb-3">
                      {supabaseConfigured ? `Supabase 全量数据，共 ${visitors.length} 条，按时间倒序` : `本设备 localStorage，共 ${visitors.length} 条`}
                    </p>
                    <div className="space-y-2">
                      {pageItems.map((v) => (
                        <div key={v.id} className="bg-gray-50 rounded-xl p-3 text-xs">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono font-semibold text-gray-800 flex items-center gap-1">
                              <Wifi size={11} className="text-blue-500" />{v.ip}
                            </span>
                            <span className="text-gray-400 flex items-center gap-0.5">
                              <Clock size={10} />{fmtTime(v.timestamp)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-gray-600">
                            <span className="flex items-center gap-1 truncate">
                              <MapPin size={10} className="text-red-400 flex-shrink-0" />
                              {[v.city, v.country].filter(Boolean).join(', ') || '—'}
                            </span>
                            <span className="flex items-center gap-1"><DeviceIcon device={v.device} />{v.device}</span>
                            <span className="truncate">{v.browser} {v.browser_version}</span>
                            <span className="truncate text-gray-500">{v.os}</span>
                            {v.isp && <span className="col-span-2 text-gray-400 truncate">{v.isp}</span>}
                            {v.referrer && v.referrer !== '(direct)' &&
                              <span className="col-span-2 text-gray-400 truncate">↩ {v.referrer}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    {pageCount > 1 && (
                      <div className="flex items-center justify-between mt-3">
                        <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                          className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors">
                          <ChevronLeft size={14} className="text-gray-600" />
                        </button>
                        <span className="text-xs text-gray-400">{page + 1} / {pageCount}</span>
                        <button onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page === pageCount - 1}
                          className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors">
                          <ChevronRight size={14} className="text-gray-600" />
                        </button>
                      </div>
                    )}
                  </>
              }
            </div>
          )}

          {/* ─ Current visitor ─ */}
          {tab === 'current' && (
            <div>
              {!current
                ? <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                    <Info size={28} className="mb-2 opacity-30" /><p className="text-sm">无记录</p>
                  </div>
                : <div className="space-y-2">
                    <p className="text-[10px] text-gray-400 mb-3">最新一次访问（当前 Session）</p>
                    <div className="bg-blue-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-blue-700 mb-1.5 flex items-center gap-1"><Wifi size={12} />网络 / 地理</p>
                      <Row label="IP 地址"    value={current.ip} />
                      <Row label="归属地"     value={[current.city, current.region, current.country].filter(Boolean).join(', ')} />
                      <Row label="时区"       value={current.timezone} />
                      <Row label="运营商/ISP" value={current.isp} />
                    </div>
                    <div className="bg-purple-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-purple-700 mb-1.5 flex items-center gap-1"><Monitor size={12} />设备 / 浏览器</p>
                      <Row label="设备类型"   value={current.device} />
                      <Row label="操作系统"   value={current.os} />
                      <Row label="浏览器"     value={`${current.browser} ${current.browser_version}`} />
                      <Row label="系统语言"   value={current.language} />
                      <Row label="屏幕分辨率" value={current.screen_resolution} />
                      <Row label="视口大小"   value={current.viewport} />
                    </div>
                    <div className="bg-green-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-green-700 mb-1.5 flex items-center gap-1"><Globe size={12} />来源 / 时间</p>
                      <Row label="来源页面" value={current.referrer} />
                      <Row label="访问页面" value={current.page_url} />
                      <Row label="访问时间" value={fmtTime(current.timestamp)} />
                    </div>
                  </div>
              }
            </div>
          )}

          {/* ─ Setup ─ */}
          {tab === 'setup' && (
            <div className="space-y-4">
              {/* Status */}
              <div className={`flex items-start gap-3 p-3 rounded-xl ${supabaseConfigured ? 'bg-green-50' : 'bg-amber-50'}`}>
                {supabaseConfigured
                  ? <CheckCircle2 size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                  : <AlertCircle  size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />}
                <div>
                  <p className={`text-sm font-semibold ${supabaseConfigured ? 'text-green-800' : 'text-amber-800'}`}>
                    {supabaseConfigured ? 'Supabase 已连接' : 'Supabase 未配置（使用本地存储）'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {supabaseConfigured
                      ? '所有访客数据正在实时写入 Supabase 数据库，跨设备可查。'
                      : '配置后所有访客 IP、地理位置、设备信息将集中存储到云端。'}
                  </p>
                </div>
              </div>

              {!supabaseConfigured && (
                <>
                  {/* Step 1 */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">1</span>
                      <span className="text-xs font-semibold text-gray-700">注册 Supabase（免费）</span>
                    </div>
                    <div className="px-4 py-3 text-xs text-gray-600 space-y-1">
                      <p>访问 <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">supabase.com</a>，点击 "Start your project" 免费注册</p>
                      <p>创建一个新 Project（选择离用户最近的区域，如 Singapore）</p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">2</span>
                      <span className="text-xs font-semibold text-gray-700">在 SQL Editor 运行建表脚本</span>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-xs text-gray-500 mb-2">进入 Supabase 控制台 → SQL Editor → New Query，粘贴以下 SQL 并运行：</p>
                      <div className="relative">
                        <pre className="bg-gray-900 text-green-400 text-[10px] p-3 rounded-lg overflow-x-auto leading-relaxed font-mono whitespace-pre-wrap">{SETUP_SQL}</pre>
                        <div className="absolute top-2 right-2">
                          <CopyButton text={SETUP_SQL} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">3</span>
                      <span className="text-xs font-semibold text-gray-700">获取 API 密钥</span>
                    </div>
                    <div className="px-4 py-3 text-xs text-gray-600 space-y-1">
                      <p>Supabase 控制台 → Settings → API</p>
                      <p>复制 <strong>Project URL</strong> 和 <strong>anon public</strong> 密钥</p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">4</span>
                      <span className="text-xs font-semibold text-gray-700">创建 .env.local 配置文件</span>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-xs text-gray-500 mb-2">在项目根目录创建 <code className="bg-gray-100 px-1 rounded">.env.local</code>，填入以下内容：</p>
                      <div className="relative">
                        <pre className="bg-gray-900 text-green-400 text-[10px] p-3 rounded-lg font-mono">{`VITE_SUPABASE_URL=https://你的项目ID.supabase.co\nVITE_SUPABASE_ANON_KEY=eyJhbGci...你的anon key`}</pre>
                        <div className="absolute top-2 right-2">
                          <CopyButton text={`VITE_SUPABASE_URL=https://你的项目ID.supabase.co\nVITE_SUPABASE_ANON_KEY=eyJhbGci...`} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">5</span>
                      <span className="text-xs font-semibold text-gray-700">重新编译部署</span>
                    </div>
                    <div className="px-4 py-3">
                      <div className="relative">
                        <pre className="bg-gray-900 text-green-400 text-[10px] p-3 rounded-lg font-mono">bash deploy.sh</pre>
                        <div className="absolute top-2 right-2"><CopyButton text="bash deploy.sh" /></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">之后每次有用户访问，详细信息会自动写入 Supabase，可在控制台 Table Editor 中实时查看。</p>
                    </div>
                  </div>
                </>
              )}

              {supabaseConfigured && (
                <div className="bg-green-50 rounded-xl p-4 text-xs text-gray-700 space-y-1">
                  <p className="font-semibold text-green-800 mb-2">数据查看方式</p>
                  <p>• Supabase 控制台 → Table Editor → visitors 表</p>
                  <p>• 开发者面板 "总览" / "记录" 标签页实时展示</p>
                  <p>• 浏览器控制台：<code className="bg-white px-1 rounded">window.__foleyVisits</code></p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-gray-100 flex-shrink-0 bg-gray-50 flex items-center justify-between">
          <span className="text-[10px] text-gray-400">
            {supabaseConfigured ? 'Supabase · ipwho.is · CounterAPI' : '本地 localStorage · ipwho.is · CounterAPI'}
          </span>
          <button
            onClick={() => { if (confirm('清除本设备的所有本地访问记录？')) { localStorage.removeItem('__foley_visits'); setVisitors([]); } }}
            className="text-[10px] text-red-400 hover:text-red-600 transition-colors">
            清除本地记录
          </button>
        </div>
      </div>
    </div>
  );
}
